import React, { useCallback, useEffect, useState } from 'react';
import { Table } from '@DesignSystem/tables';
import {
  ASSET_COLUMNS_NAME,
  ASSET_TABLE_COLUMNS,
  ASSET_TABLE_COLUMNS_WIDTH,
  ASSET_TABLE_PAGE_SIZES
} from './artifactVersionAssetsTableConfig';
import ArtifactVersionAssetsEmptyState from './ArtifactVersionAssetsEmptyState';
import styles from './ArtifactVersionAssetsTable.module.scss';
import { DataTypeProvider } from '@devexpress/dx-react-grid';
import { renderActionsCell, renderNameCell, renderSizeCell } from './helpers';
import { ArtifactVersionAsset } from '@artifacts/types/ArtifactVersionAsset';
import { useVersionAssetsPaginationContext } from '@artifacts/contexts';
import { debounce } from 'lodash';
import ValueFormatterProps = DataTypeProvider.ValueFormatterProps;

type ArtifactVersionAssetsTableProps = {
  rows: ArtifactVersionAsset[];
  isFetching: boolean;
  handleRowClick: (assetId: string) => void;
  experimentKey: string;
  artifactVersionId: string;
  totalRowCount: number;
};

const DEBOUNCE_DELAY = 500;

const ArtifactVersionAssetsTable: React.FC<ArtifactVersionAssetsTableProps> = ({
  rows,
  totalRowCount,
  isFetching,
  experimentKey,
  artifactVersionId,
  handleRowClick
}) => {
  const {
    searchQuery,
    setSearchQuery,
    currentPage: contextCurrentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortOrder,
    setSortOrder,
    sortBy,
    setSortBy
  } = useVersionAssetsPaginationContext();

  const [localCurrentPage, setLocalCurrentPage] = useState(contextCurrentPage);

  const dataTypes = [
    {
      cols: [ASSET_COLUMNS_NAME.ACTIONS],
      cell: ({ row }: ValueFormatterProps) =>
        renderActionsCell(row, experimentKey, artifactVersionId)
    },
    {
      cols: [ASSET_COLUMNS_NAME.NAME],
      cell: ({ row }: ValueFormatterProps) => renderNameCell(row)
    },
    {
      cols: [ASSET_COLUMNS_NAME.SIZE],
      cell: ({ row }: ValueFormatterProps) => renderSizeCell(row)
    }
  ];

  const debouncedSetCurrentPage = useCallback(
    debounce(page => {
      setCurrentPage(page);
    }, DEBOUNCE_DELAY),
    []
  );

  useEffect(() => {
    debouncedSetCurrentPage(localCurrentPage);
  }, [localCurrentPage, debouncedSetCurrentPage]);

  return (
    <Table
      columns={ASSET_TABLE_COLUMNS}
      dataTypes={dataTypes}
      rows={rows}
      className={styles.artifactVersionAssetsTable}
      rowHeight={50}
      rowIdKey="assetId"
      maxHeight={360}
      selectionConfig={{ isDisabled: true }}
      columnWidthsConfig={{
        isDisabled: true,
        columnExtensions: ASSET_TABLE_COLUMNS_WIDTH
      }}
      columnOrderConfig={{ isDisabled: true }}
      paginationConfig={{
        isDisabled: false,
        isServerSidePagination: true,
        pageSize: itemsPerPage,
        pageSizes: ASSET_TABLE_PAGE_SIZES,
        pageNumber: localCurrentPage,
        onPageNumberChange: setLocalCurrentPage,
        onPageSizeChange: (number: number) => {
          setItemsPerPage(number);
          setCurrentPage(0);
        }
      }}
      sortingConfig={{
        isDisabled: false,
        columnSorting: [{ columnName: sortBy, direction: sortOrder }],
        onSortingChange: (
          values: { columnName: string; direction: 'asc' | 'desc' }[]
        ) => {
          values.forEach(({ columnName, direction }) => {
            setSortBy(columnName);
            setSortOrder(direction);
          });
        }
      }}
      onRowClick={row => handleRowClick(row)}
      isFetching={isFetching}
      totalRowCount={totalRowCount}
      shouldRemoveStubCells
      renderEmptyState={props => (
        <ArtifactVersionAssetsEmptyState
          {...props}
          searchValue={searchQuery}
          resetSearch={() => setSearchQuery('')}
        />
      )}
    />
  );
};

export default ArtifactVersionAssetsTable;
